import { useState, type ChangeEvent, type FormEvent } from 'react';
import { AlertCircle, Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface AdminCreateFormValues {
  username: string;
  email: string;
  mobile: string;
  password: string;
  confirmPassword: string;
}

interface AdminCreateDialogProps {
  open: boolean;
  values: AdminCreateFormValues;
  fieldErrors: Record<string, string>;
  generalError: string | null;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (values: AdminCreateFormValues) => void;
  onSubmit: () => void;
}

const fields: Array<{
  name: keyof AdminCreateFormValues;
  label: string;
  type: string;
  autoComplete: string;
  placeholder: string;
}> = [
  { name: 'username', label: 'Όνομα χρήστη', type: 'text', autoComplete: 'off', placeholder: 'π.χ. maria.p' },
  { name: 'email', label: 'Email', type: 'email', autoComplete: 'off', placeholder: 'name@diathermiki.gr' },
  { name: 'mobile', label: 'Κινητό', type: 'tel', autoComplete: 'off', placeholder: '+30 69…' },
];

export function AdminCreateDialog({
  open,
  values,
  fieldErrors,
  generalError,
  isSubmitting,
  onOpenChange,
  onChange,
  onSubmit,
}: AdminCreateDialogProps) {
  const [showPasswords, setShowPasswords] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...values, [event.target.name]: event.target.value });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto p-0 sm:max-w-lg">
        <form onSubmit={handleSubmit} noValidate>
          <DialogHeader className="border-b border-slate-100 px-5 py-5 text-left sm:px-6">
            <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-50 text-rose-700 ring-1 ring-rose-100">
              <ShieldCheck className="h-5 w-5" aria-hidden="true" />
            </div>
            <DialogTitle className="text-xl tracking-[-0.02em]">Νέος διαχειριστής</DialogTitle>
            <DialogDescription className="max-w-md leading-6">
              Δημιουργήστε έναν προσωπικό λογαριασμό. Μην χρησιμοποιείτε κοινόχρηστα στοιχεία σύνδεσης.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 px-5 py-5 sm:px-6">
            {generalError && (
              <Alert variant="destructive" className="bg-rose-50">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Δεν ολοκληρώθηκε η δημιουργία</AlertTitle>
                <AlertDescription>{generalError}</AlertDescription>
              </Alert>
            )}

            {fields.map((field) => (
              <div className="space-y-2" key={field.name}>
                <Label htmlFor={`admin-${field.name}`}>{field.label}</Label>
                <Input
                  id={`admin-${field.name}`}
                  name={field.name}
                  type={field.type}
                  value={values[field.name]}
                  onChange={handleChange}
                  autoComplete={field.autoComplete}
                  placeholder={field.placeholder}
                  aria-invalid={Boolean(fieldErrors[field.name])}
                  aria-describedby={fieldErrors[field.name] ? `admin-${field.name}-error` : undefined}
                  className="h-11 border-slate-200 bg-slate-50/70 focus:bg-white"
                />
                {fieldErrors[field.name] && (
                  <p id={`admin-${field.name}-error`} className="text-xs font-medium text-rose-700">
                    {fieldErrors[field.name]}
                  </p>
                )}
              </div>
            ))}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="admin-password">Κωδικός</Label>
                <div className="relative">
                  <Input
                    id="admin-password"
                    name="password"
                    type={showPasswords ? 'text' : 'password'}
                    value={values.password}
                    onChange={handleChange}
                    autoComplete="new-password"
                    minLength={12}
                    maxLength={128}
                    aria-invalid={Boolean(fieldErrors.password)}
                    aria-describedby={fieldErrors.password ? 'admin-password-error' : 'admin-password-hint'}
                    className="h-11 border-slate-200 bg-slate-50/70 pr-11 focus:bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords((current) => !current)}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-md text-slate-400 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-rose-500"
                    aria-label={showPasswords ? 'Απόκρυψη κωδικών' : 'Εμφάνιση κωδικών'}
                  >
                    {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p id={fieldErrors.password ? 'admin-password-error' : 'admin-password-hint'} className={`text-xs ${fieldErrors.password ? 'font-medium text-rose-700' : 'text-slate-500'}`}>
                  {fieldErrors.password || 'Τουλάχιστον 12 χαρακτήρες.'}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-confirm-password">Επιβεβαίωση</Label>
                <Input
                  id="admin-confirm-password"
                  name="confirmPassword"
                  type={showPasswords ? 'text' : 'password'}
                  value={values.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  aria-invalid={Boolean(fieldErrors.confirmPassword)}
                  aria-describedby={fieldErrors.confirmPassword ? 'admin-confirm-password-error' : undefined}
                  className="h-11 border-slate-200 bg-slate-50/70 focus:bg-white"
                />
                {fieldErrors.confirmPassword && (
                  <p id="admin-confirm-password-error" className="text-xs font-medium text-rose-700">
                    {fieldErrors.confirmPassword}
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 border-t border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
            <Button type="button" variant="outline" className="h-11" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Άκυρο
            </Button>
            <Button type="submit" className="h-11 bg-slate-950 text-white hover:bg-slate-800" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {isSubmitting ? 'Δημιουργία…' : 'Δημιουργία λογαριασμού'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
